<script setup lang="ts">
import type {
  NavigationPageOption,
  NavigationPageScale,
} from "#bb/types/blocks/navigation/NavigationPageOption";
import type { NavigationPageRenderFeatures } from "#bb/composables/useNavigationPageItems";
import { eventBus } from "#bb/lib/eventBus";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) as NavigationPageOption);

const features = inject<NavigationPageRenderFeatures>("render-features", {});

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Navigation page editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the navigation page",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Navigation Page",
    icon: "lucide:panel-top-open",
  },
});

const scale = computed<NavigationPageScale>(
  () => definitionData.value?.scale ?? "md"
);

const scaleClasses: Record<NavigationPageScale, { title: string; description: string; icon: string }> = {
  sm: {
    title: "text-lg",
    description: "text-sm",
    icon: "size-10",
  },
  md: {
    title: "text-xl",
    description: "text-base",
    icon: "size-12",
  },
  lg: {
    title: "text-2xl",
    description: "text-lg",
    icon: "size-14",
  },
  xl: {
    title: "text-3xl",
    description: "text-xl",
    icon: "size-16",
  },
};

const { items } = useNavigationPageItems(
  definitionData,
  features,
  "NavigationPage",
);
</script>

<template>
  <template v-for="item in items" :key="item.id">
    <div
      v-if="features.editor || item.disabled"
      class="block h-full"
      :aria-disabled="item.disabled || undefined"
    >
      <Card :class="['flex h-full flex-col', item.classes.card]">
        <CardHeader>
          <CardTitle :class="scaleClasses[scale].title">{{ item.title }}</CardTitle>
          <CardDescription :class="[scaleClasses[scale].description, item.classes.description]">
            {{ item.description }}
          </CardDescription>
        </CardHeader>
        <CardContent class="mt-auto flex justify-end">
          <img v-if="item.image" :src="item.image" :alt="item.title || 'Navigation item image'" />
          <Icon
            v-else
            :name="item.icon"
            mode="svg"
            :class="[scaleClasses[scale].icon, item.classes.icon]"
            :style="{ color: item.iconColor || undefined }"
          />
        </CardContent>
      </Card>
    </div>

    <NuxtLink
      v-else
      :to="item.to"
      :external="item.external"
      :target="item.external ? '_blank' : undefined"
      :rel="item.external ? 'noopener noreferrer' : undefined"
      class="group block h-full cursor-pointer focus-visible:outline-none"
    >
      <Card :class="['flex h-full flex-col transition-colors group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2', item.classes.card, item.classes.interactive]">
        <CardHeader>
          <CardTitle :class="scaleClasses[scale].title">{{ item.title }}</CardTitle>
          <CardDescription :class="[scaleClasses[scale].description, item.classes.description]">
            {{ item.description }}
          </CardDescription>
        </CardHeader>
        <CardContent class="mt-auto flex justify-end">
          <img v-if="item.image" :src="item.image" :alt="item.title || 'Navigation item image'" />
          <Icon
            v-else
            :name="item.icon"
            mode="svg"
            :class="[scaleClasses[scale].icon, item.classes.icon]"
            :style="{ color: item.iconColor || undefined }"
          />
        </CardContent>
      </Card>
    </NuxtLink>
  </template>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.NAVIGATION_PAGE,
  icon: "lucide:panel-top-open",
  type: "Navigation Page",
  description: "A grid of navigation cards linking to pages or external destinations.",
  editor: () => import("#bb/components/editor/navigation/page.vue"),
  default: {
    scale: "md",
    displayStates: [
      {
        id: "default",
        name: "Default",
        interactive: true,
        classes: {
          card: "",
          description: "",
          icon: "",
          interactive: "group-hover:bg-accent/20",
        },
      },
      {
        id: "current",
        name: "Current",
        interactive: true,
        classes: {
          card: "border-primary bg-primary text-primary-foreground",
          description: "text-primary-foreground/80",
          icon: "text-primary-foreground",
          interactive: "group-hover:bg-primary/90",
        },
      },
      {
        id: "completed",
        name: "Completed",
        interactive: true,
        classes: {
          card: "border-success/40 bg-muted",
          description: "",
          icon: "text-success",
          interactive: "group-hover:bg-muted/80",
        },
      },
      {
        id: "locked",
        name: "Locked",
        interactive: true,
        classes: {
          card: "border-border bg-muted/60 text-muted-foreground",
          description: "text-muted-foreground",
          icon: "text-muted-foreground",
          interactive: "group-hover:bg-muted/80",
        },
      },
    ],
    items: [
      {
        id: "navigation-item-1",
        title: "Dashboard",
        description: "Open the main dashboard experience.",
        icon: "lucide:layout-dashboard",
        image: "",
        url: "/dashboard",
        external: false,
        interactive: true,
        displayStateId: "default",
      },
    ],
  },
  categories: ["Navigation"],
  order: 160101,
};
</script>